/**
 * Thin HTTP layer over `services/attractions-service`.
 *
 * This is the ONLY module that knows the service's wire shape; everything else
 * consumes the UI types in `../config` via `mapper.ts`. Same split the hotels
 * and restaurants domains use, so this becomes a drop-in for an
 * `AttractionsClient` in `@wl/api-client` when one is added — nothing outside
 * `_data/` will need to change.
 *
 * Errors are swallowed into `null` / empty results ON PURPOSE: the catalogue is
 * a browsing surface, and a service blip should render "no attractions found"
 * rather than a 500 page. Anything that must fail loudly (the booking funnel)
 * goes through the cart endpoints, not here.
 */

import { ATTRACTIONS_API_BASE_URL, type ListingFacets } from '../config';
import { apiFetch } from '@wl/api-client';

/** A product record exactly as the service returns it (`toPublic` applied). */
export interface AttractionRecord {
  _id?: string;
  uuid: string;
  title: string;
  description?: string;
  mainPhotoUrl?: string;
  photos?: string[];
  currency?: string;
  price?: number | null;
  isFree?: boolean;
  rating?: number | null;
  numberOfReviews?: number | null;
  duration?: string | null;
  durationLabel?: string | null;
  city?: string;
  country?: string;
  address?: string;
  geo?: { latitude?: number; longitude?: number };
  categories?: string[];
  availabilityType?: string | null;
  isAccessible?: boolean;
  hotelPickup?: boolean;
  importantInformation?: string | null;
  includedList?: string[];
  excludedList?: string[];
  cancellationPolicy?: string | null;
  slug: string;
  citySlug?: string;
  countrySlug?: string;

  /* Detail-page content, mirrored from Bridgify's `additional_info` block.
   * All optional: coverage is genuinely uneven at the source (highlights 34%,
   * reviews 10%, opening hours ~0%), and a document mirrored before these
   * existed carries none — run `POST /sync/attractions` to backfill. Each
   * section on the page hides itself when its field is empty. */
  infoFields?: AttractionInfoField[];
  highlights?: string[];
  fullDescription?: string | null;
  scheduleBlocks?: AttractionScheduleBlock[];
  reviews?: AttractionReviewRecord[];
  freeCancellation?: boolean;
  supportedLanguages?: string[];
  skipTheLine?: boolean;
  wheelchairAccessible?: boolean;
  /* BCP-2066 §6 provider sections — backfilled by `POST /sync/attractions`. */
  instructions?: string[];
  landmarks?: string[];
  itinerary?: AttractionItinerary | null;

  /* Derived facet + ranking fields, computed by the service at sync time.
   * Optional because a document mirrored before they existed carries none —
   * see `POST /sync/scores`, the backfill — and `mapper.ts` falls back rather
   * than rendering a product with no type at all. */
  types?: string[];
  features?: string[];
  durationBucket?: string | null;
  startTimeBuckets?: string[];
  /** `0` for free, `null` when the supplier does not quote up front. */
  filterPrice?: number | null;
  recommendationScore?: number;
}

/** Wire shapes for the blocks above — the UI types live in `../config`. */
export interface AttractionInfoField {
  key: string;
  label: string;
  value: string;
  icon: string;
  mapsQuery?: string;
}

export interface AttractionItinerary {
  start: string | null;
  stops: { description: string; durationLabel: string | null; admissionIncluded: boolean | null }[];
  end: string | null;
}

export interface AttractionScheduleBlock {
  key: string;
  label: string;
  icon: string;
  rows: { label: string; value: string }[];
  lines: string[];
}

export interface AttractionReviewRecord {
  id: string;
  author: string;
  date: string | null;
  rating: number | null;
  title: string | null;
  text: string;
}

export interface AttractionSearchResponse {
  message: string;
  data: AttractionRecord[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  /**
   * Present on the FIRST page of a non-typeahead search only — the panel is
   * already rendered and unchanged when infinite scroll fetches page 2, so the
   * service skips the aggregation there.
   */
  facets?: ListingFacets;
  /** A city search's freshness (on-demand catalogue) — page 1 only. */
  cityStatus?: CityStatus;
}

/**
 * How current a searched city's products are. `loading` means the first read
 * is still running and the results are PARTIAL; the listing polls
 * `fetchCitySyncStatus` and refreshes when it ends.
 */
export interface CityStatus {
  state: 'fresh' | 'refreshing' | 'loading' | 'error';
  syncedAt: string | null;
  expectedTotal: number | null;
}

export async function fetchCitySyncStatus(cityId: number): Promise<CityStatus | null> {
  const res = await get<{ data: CityStatus }>(`/attractions/cities/${cityId}/sync-status`);
  return res?.data ?? null;
}

export interface AttractionCityRecord {
  /**
   * Bridgify's canonical city id — the AUTHORITATIVE destination identity.
   *
   * The same number the service stores as `Attraction.cityId` and that
   * `/attractions/search-suggestion/?search_base=city` returns as `id`, so the
   * value chosen here is the value that filters the listing and builds the
   * facets. Prefer it over `city`/`citySlug` everywhere a destination is being
   * IDENTIFIED rather than DISPLAYED: twelve destination names in the
   * catalogue map to two different Bridgify cities each.
   *
   * `null` for the destinations Bridgify ships with no id at all (~730 active
   * products — "Crete", "Cappadocia"); those still key on the slug.
   */
  cityId?: number | null;
  city: string;
  citySlug: string;
  /**
   * Unique readable slug — what the user-facing URL carries. Differs from
   * `citySlug` only for destinations whose slug is contested by another
   * stocked city (`las-vegas` / `las-vegas-new-mexico`).
   */
  urlSlug?: string;
  /**
   * Administrative area ("Greater London") shown above the country on a
   * suggestion row.
   *
   * OPTIONAL because the catalogue does not carry one today: Bridgify exposes
   * only `external_city_name` / `external_country_name`, so `Attractions_Cities`
   * has no region column and the sub-line renders as the country alone. Read
   * through `cityRegionLine()`, which drops the segment when it is absent, so
   * populating it later is a data change rather than a UI change.
   */
  region?: string;
  country?: string;
  countrySlug?: string;
  /**
   * How many products WE hold for this destination.
   *
   * Zero is a real, meaningful value here: a suggestion may come straight from
   * Bridgify's global city list for a place we do not sell. The listing names
   * such a destination and says it has no attractions, rather than silently
   * showing another city's results.
   */
  attractionCount: number;
}

/**
 * A Bridgify point-of-interest suggestion
 * (`/attractions/search-suggestion/?search_base=place`).
 *
 * Deliberately HAS NO ID — the integration spec warns that place results carry
 * none, and the live payload confirms it. A place can therefore only become a
 * free-text listing query, never a link to a product.
 */
export interface AttractionPlaceRecord {
  name: string;
  slug: string | null;
  thumbnail: string | null;
  cityId: number | null;
  cityName: string | null;
  countryName: string | null;
}

/**
 * The muted second line of a location suggestion — "Greater London, United
 * Kingdom", or just "United Kingdom" while the catalogue has no region.
 */
export function cityRegionLine(city: Pick<AttractionCityRecord, 'region' | 'country'>): string {
  return [city.region, city.country].filter(Boolean).join(', ');
}

/**
 * Availability PREVIEW. The service labels this `authoritative: false` — the
 * dates offered inside the cart come from a live supplier check and can
 * disagree, so this may only ever drive a browsing calendar.
 */
export interface AvailabilityPreview {
  dates: Record<string, string[]>;
  availabilityType: string | null;
  authoritative: boolean;
  hasAvailabilityData: boolean;
}

/** Query params the service's `SearchAttractionsDto` accepts. */
export interface AttractionSearchParams {
  search?: string;
  /**
   * Restricts `search` to the product TITLE and puts the request on the
   * service's tier-ranked typeahead path (BCP-2056 §3/§5). Without it the
   * service also matches descriptions and orders by rating.
   */
  titleOnly?: boolean;
  /**
   * Canonical Bridgify city id. Wins over `city`/`citySlug` server-side — send
   * it whenever the destination came from a suggestion rather than raw typing.
   */
  cityId?: number;
  city?: string;
  citySlug?: string;
  countrySlug?: string;
  category?: string;
  availabilityType?: string;
  minPrice?: number;
  maxPrice?: number;
  minRating?: number;
  accessibleOnly?: boolean;
  hotelPickupOnly?: boolean;

  /* Sidebar multi-selects. Sent as repeated params (`?types=a&types=b`). */
  types?: string[];
  features?: string[];
  languages?: string[];
  durations?: string[];
  startTimes?: string[];
  guestRatings?: number[];

  sortBy?: 'recommended' | 'price' | 'rating' | 'title';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

const BASE = () => ATTRACTIONS_API_BASE_URL;

/**
 * `no-store` because every catalogue surface is `force-dynamic` and prices /
 * availability move underneath us; caching here would resurrect stale money.
 */
async function get<T>(path: string, signal?: AbortSignal): Promise<T | null> {
  const base = BASE();
  if (!base) {
    console.error('[attractions] ATTRACTIONS_API_BASE_URL is not configured');
    return null;
  }
  try {
    const res = await apiFetch(`${base}${path}`, { cache: 'no-store', signal });
    if (!res.ok) {
      console.error(`[attractions] GET ${path} → ${res.status}`);
      return null;
    }
    return (await res.json()) as T;
  } catch (err) {
    // An abort is the expected outcome of typing another character, not a
    // failure — swallow it silently and let the caller's own guard ignore the
    // result, rather than logging an error per keystroke.
    if ((err as Error).name === 'AbortError') return null;
    console.error(`[attractions] GET ${path} failed: ${(err as Error).message}`);
    return null;
  }
}

/**
 * Only defined values are appended. The service runs `forbidNonWhitelisted`, so
 * an empty-string param is not merely useless — `city=` is a declared field and
 * would filter the result set down to products with a blank city.
 */
function qs(params: AttractionSearchParams): string {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    // A multi-select goes out as repeated params. An EMPTY array is skipped
    // rather than sent as `types=`: the service declares that field, so an
    // empty value would filter for products whose type is the empty string and
    // return nothing.
    if (Array.isArray(value)) {
      for (const entry of value) {
        if (entry === undefined || entry === null || entry === '') continue;
        sp.append(key, String(entry));
      }
      continue;
    }
    sp.set(key, String(value));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export async function fetchAttractions(
  params: AttractionSearchParams = {},
  signal?: AbortSignal,
): Promise<AttractionSearchResponse> {
  const res = await get<AttractionSearchResponse>(`/attractions${qs(params)}`, signal);
  return res ?? { message: 'error', data: [], total: 0, page: 1, limit: 0, totalPages: 0 };
}

export async function fetchAttractionBySlug(slug: string): Promise<AttractionRecord | null> {
  const res = await get<{ data: AttractionRecord }>(
    `/attractions/by-slug-only/${encodeURIComponent(slug)}`,
  );
  return res?.data ?? null;
}

export async function fetchAttractionByUuid(uuid: string): Promise<AttractionRecord | null> {
  const res = await get<{ data: AttractionRecord }>(`/attractions/${encodeURIComponent(uuid)}`);
  return res?.data ?? null;
}

export async function fetchCities(): Promise<AttractionCityRecord[]> {
  const res = await get<{ data: AttractionCityRecord[] }>('/attractions/cities');
  return res?.data ?? [];
}

/**
 * Destination typeahead — the same `/attractions/cities` endpoint, tier-ranked
 * server-side once `q` is supplied (`CitiesQueryDto` accepts `q` and `limit`
 * ONLY; the service runs `forbidNonWhitelisted`, so anything else 400s).
 */
/**
 * One destination by its canonical Bridgify id.
 *
 * The listing's resolution step. Returns null when we hold nothing for that id
 * — the caller must render that honestly rather than substituting another city.
 *
 * Implemented over the existing `/attractions/cities` facet rather than a new
 * endpoint: it is one small collection, already tenant-scoped, and already the
 * single source of what destinations exist.
 */
export async function fetchCityById(cityId: number): Promise<AttractionCityRecord | null> {
  const rows = await fetchCities();
  return rows.find((c) => c.cityId === cityId) ?? null;
}

/**
 * Resolve the readable `?city=` value back to a destination — and so to its
 * canonical `cityId`.
 *
 * Accepts the unique `urlSlug` we emit today, the plain `citySlug` from an
 * older link, or the display name from the original URL format. The service
 * does the matching (and the highest-inventory tie-break for an ambiguous
 * slug); this is a thin pass-through so the rule lives in one place.
 */
export async function fetchCityBySlugOrName(
  value: string,
): Promise<AttractionCityRecord | null> {
  const sp = new URLSearchParams({ slug: value });
  const res = await get<{ data: AttractionCityRecord | null }>(
    `/attractions/cities/resolve?${sp.toString()}`,
  );
  return res?.data ?? null;
}

export async function fetchCitySuggestions(
  q: string,
  limit: number,
  signal?: AbortSignal,
): Promise<AttractionCityRecord[]> {
  const sp = new URLSearchParams({ q, limit: String(limit) });
  const res = await get<{ data: AttractionCityRecord[] }>(
    `/attractions/cities?${sp.toString()}`,
    signal,
  );
  return res?.data ?? [];
}

/**
 * Attraction/POI suggestions for one destination, straight from Bridgify.
 *
 * Scoped by `cityId`, which is what keeps it honest — without it Bridgify
 * searches every city on earth. Unused by the hero today (its rows have no id,
 * so they cannot deep-link to a product); exposed for surfaces that want
 * supplier POI names.
 */
export async function fetchPlaceSuggestions(
  q: string,
  cityId: number | undefined,
  limit: number,
  signal?: AbortSignal,
): Promise<AttractionPlaceRecord[]> {
  const sp = new URLSearchParams({ q, limit: String(limit) });
  if (cityId !== undefined) sp.set('cityId', String(cityId));
  const res = await get<{ data: AttractionPlaceRecord[] }>(
    `/attractions/place-suggestions?${sp.toString()}`,
    signal,
  );
  return res?.data ?? [];
}

/**
 * Whether the product has any slot to sell (TSL products only; `null` =
 * unknown / not applicable — sell as normal). Certification §6.
 */
export async function fetchBookable(uuid: string): Promise<boolean | null> {
  const res = await get<{ data: { bookable: boolean | null } | null }>(
    `/attractions/${encodeURIComponent(uuid)}/bookable`,
  );
  return res?.data?.bookable ?? null;
}

export async function fetchAvailability(
  uuid: string,
  dateFrom: string,
  dateTo: string,
): Promise<AvailabilityPreview | null> {
  const res = await get<{ data: AvailabilityPreview | null }>(
    `/attractions/${encodeURIComponent(uuid)}/availability?dateFrom=${dateFrom}&dateTo=${dateTo}`,
  );
  return res?.data ?? null;
}
